/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */

import cx from 'classnames';
import SVG from 'react-inlinesvg';
import check from 'assets/icons/check.svg';
import minus from 'assets/icons/minus.svg';
import Tooltip from 'lib/Tooltip';
import Chip from 'lib/Chip';
import { Color, Size } from 'lib/Chip/Chip.types';

export const INDETERMINATE = 'indeterminate';

export const multiSelectCheckboxStyle = {
  notSelected: 'bg-neutral-50 border-neutral-200',
  selected: 'bg-primary-700 border-transparent text-white',
};

export const checkboxStyle = {
  base: 'border border-neutral-300 rounded shadow-inner-sm shrink-0',
  size: 'w-4 h-4',
  icon: 'w-3 h-3 ml-px mt-px',
  disabledUnselected: 'bg-neutral-950-opacity-5 border-neutral-950-opacity-5',
  disabledSelected: 'bg-neutral-400 border-transparent text-white',
};

type CheckboxProps = {
  isSelected: boolean | typeof INDETERMINATE;
  dataTestId?: string;
  dataPendoId?: string;
  onChange?: (newValue: boolean) => void;
  onChangeWithValue?: (value: string | number, isChecked: boolean) => void;
  label?: React.ReactNode;
  name?: string;
  value?: string | number;
  isDisabled?: boolean;
  chipText?: string;
};

const getCheckboxSquareClassName = (isSelected: CheckboxProps['isSelected'], isDisabled: boolean): string =>
  cx(checkboxStyle.base, checkboxStyle.size, {
    'cursor-pointer': !isDisabled,
    [multiSelectCheckboxStyle.notSelected]: !isSelected && !isDisabled,
    [multiSelectCheckboxStyle.selected]: isSelected && !isDisabled,
    [checkboxStyle.disabledUnselected]: !isSelected && isDisabled,
    [checkboxStyle.disabledSelected]: isSelected && isDisabled,
  });

const Checkbox = ({
  isSelected,
  onChange,
  onChangeWithValue,
  dataTestId,
  dataPendoId,
  label,
  name,
  value,
  isDisabled = false,
  chipText,
}: CheckboxProps): React.ReactElement => {
  const checkedValue = isSelected === INDETERMINATE ? 'mixed' : isSelected;
  const title = isSelected === INDETERMINATE ? INDETERMINATE : 'checked';
  const icon = isSelected === INDETERMINATE ? minus : check;

  const handleClick = (): void => {
    if (isDisabled) return;

    const newValue = isSelected !== true;
    onChange?.(newValue);
    if (value !== undefined) onChangeWithValue?.(value, newValue);
  };

  const checkboxSquare = (
    <div className={getCheckboxSquareClassName(isSelected, isDisabled)}>
      {isSelected ? (
        <Tooltip tooltip={title}>
          <SVG src={icon} className={checkboxStyle.icon} />
        </Tooltip>
      ) : null}
    </div>
  );

  const hasWrapper = label || chipText;

  if (!hasWrapper) {
    return (
      <div
        role="checkbox"
        onClick={handleClick}
        aria-checked={checkedValue}
        aria-disabled={isDisabled}
        data-test-id={dataTestId}
        data-pendo-id={dataPendoId}
        data-name={name}
        className={cx({ 'opacity-70 pointer-events-none': isDisabled })}
      >
        {checkboxSquare}
      </div>
    );
  }

  return (
    <div
      role="checkbox"
      onClick={handleClick}
      aria-checked={checkedValue}
      aria-disabled={isDisabled}
      data-test-id={dataTestId}
      data-pendo-id={dataPendoId}
      data-name={name}
      className={cx('flex justify-between items-center cursor-pointer', {
        'opacity-70 pointer-events-none': isDisabled,
      })}
    >
      <div className="flex items-center gap-1">
        {checkboxSquare}
        {label != null && <div className="body-base text-neutral-950 leading-5">{label}</div>}
      </div>
      {chipText ? <Chip label={chipText} size={Size.SMALL} color={Color.PRIMARY} /> : null}
    </div>
  );
};

export default Checkbox;

/* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */
